import axios, { AxiosInstance, AxiosRequestConfig, InternalAxiosRequestConfig } from 'axios';
import * as Sentry from '@sentry/react';
import { authService } from './auth-service';
import { createLogger } from './logging';

const logger = createLogger('HttpClient');

// Extend AxiosRequestConfig to include meta
interface RequestConfigWithMeta extends InternalAxiosRequestConfig {
  meta?: {
    startTime: number;
    requestId: string;
  };
}

/**
 * Generate a unique request ID for client-server correlation
 */
const generateRequestId = (): string => {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);
};

const API_URL = import.meta.env.VITE_API_URL || '';
const diagnosticErrorCodes = new Set(['ERR_NETWORK', 'ERR_CANCELED', 'ERR_BAD_RESPONSE', 'ERR_BAD_REQUEST', 'ERR_BAD_OPTION', 'ERR_BAD_OPTION_VALUE', 'ERR_NOT_SUPPORT', 'ERR_INVALID_URL', 'ERR_FR_TOO_MANY_REDIRECTS', 'ERR_DEPRECATED', 'ECONNABORTED', 'ETIMEDOUT']);

function safePath(value: unknown): string | null {
  if (typeof value !== 'string' || !value.trim()) return null;
  try { return new URL(value, 'https://request.invalid').pathname; }
  catch { return null; }
}

/** Only this primitive whitelist may enter transport logs or Sentry context. */
export function httpFailureDiagnostics(error: any) {
  const config = error?.config as RequestConfigWithMeta | undefined;
  const requestId = config?.meta?.requestId;
  const code = diagnosticErrorCodes.has(error?.code) ? error.code as string : null;
  const elapsed = typeof config?.meta?.startTime === 'number' ? performance.now() - config.meta.startTime : null;
  const build = import.meta.env.VITE_SENTRY_RELEASE;
  const method = config?.method?.toUpperCase();
  return {
    requestId: typeof requestId === 'string' && /^[A-Za-z0-9_-]{1,80}$/.test(requestId) ? requestId : null,
    path: safePath(config?.url),
    method: method && /^(GET|POST|PUT|PATCH|DELETE|HEAD|OPTIONS)$/.test(method) ? method : null,
    status: Number.isInteger(error?.response?.status) ? error.response.status : null,
    code,
    durationMs: elapsed !== null && Number.isFinite(elapsed) ? Math.max(0, Math.round(elapsed)) : null,
    online: typeof navigator !== 'undefined' && typeof navigator.onLine === 'boolean' ? navigator.onLine : null,
    responsePath: safePath(error?.request?.responseURL),
    build: typeof build === 'string' && /^[a-f0-9]{7,64}$/i.test(build) ? build : null,
  };
}

if (!API_URL) {
  logger.error('VITE_API_URL environment variable is not set');
} else {
  logger.info('Using API URL: ' + API_URL);
}

// Add a function to detect production environment
const isProduction = () => {
  return import.meta.env.PROD || import.meta.env.MODE === 'production';
};

// Create axios instance with default config
const axiosInstance: AxiosInstance = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true, // Include cookies
});

// Add request interceptor for auth and request ID correlation
axiosInstance.interceptors.request.use(async (config: RequestConfigWithMeta) => {
  const requestId = generateRequestId();
  const startTime = performance.now();
  config.meta = { startTime, requestId };

  // Add X-Request-ID header for server correlation
  config.headers.set('X-Request-ID', requestId);

  // Get authentication token if not already in headers
  if (!config.headers?.Authorization) {
    try {
      const token = await authService.getToken();
      if (token) {
        config.headers.set('Authorization', `Bearer ${token}`);
      }
    } catch (error) {
      logger.error('Error getting auth token', { error });
    }
  }

  logger.debug('Request', {
    method: config.method?.toUpperCase(),
    url: safePath(config.url),
    requestId
  });

  return config;
});

// Add response interceptor with Sentry capture for 5xx errors
axiosInstance.interceptors.response.use(
  (response) => {
    const config = response.config as RequestConfigWithMeta;
    const duration = performance.now() - (config.meta?.startTime || 0);

    logger.debug('Response', {
      method: response.config.method?.toUpperCase(),
      url: safePath(response.config.url),
      status: response.status,
      duration: `${duration.toFixed(0)}ms`,
      requestId: config.meta?.requestId
    });

    return response;
  },
  (error) => {
    const diagnostic = httpFailureDiagnostics(error);
    const { requestId, status } = diagnostic;

    // Capture failures without sending Axios config, bodies or credentials.
    if ((status && status >= 500) || (!error.response && axios.isAxiosError(error) && error.code !== 'ERR_CANCELED')) {
      Sentry.captureException(new Error(`API request failed (${status || diagnostic.code || 'no response'})`), {
        tags: { requestId: requestId || 'unavailable', statusCode: status === null ? 'no_response' : String(status) },
        extra: { http: diagnostic },
      });
    }

    // A single string remains readable in browser log collectors that flatten objects.
    logger.error(`API request failed ${JSON.stringify(diagnostic)}`);

    return Promise.reject(preserveHttpError(error));
  }
);

/**
 * Adds a useful display message without replacing the transport error.
 * Callers that deliberately handle statuses such as 422 must retain the
 * Axios response status and typed response body.
 */
export function preserveHttpError(error: any): any {
  if (error?.response?.data) {
    const serverError = error.response.data;
    error.message =
      serverError.error
      || serverError.message
      || 'An error occurred while processing your request.';
    return error;
  }

  if (axios.isAxiosError(error) && !error.response) {
    error.message =
      'Unable to connect to server. Please check your connection and try again.';
    return error;
  }

  if (error && typeof error === 'object') {
    error.message =
      error.message
      || 'An error occurred while processing your request.';
    return error;
  }

  return new Error(
    'An error occurred while processing your request.'
  );
}

/**
 * Make a request using the HTTP client
 * Token is automatically added by the request interceptor
 */
export async function httpRequest<T = any>(path: string, options: AxiosRequestConfig = {}): Promise<T> {
  const config: AxiosRequestConfig = {
    ...options,
    url: path,
    headers: {
      ...options.headers,
    }
  };

  try {
    const response = await axiosInstance.request(config);
    return response.data;
  } catch (error) {
    logger.error(`Request failed ${safePath(path) || '(unknown path)'}`);
    throw error;
  }
}

/**
 * Upload a file using FormData
 * Token is automatically added by the request interceptor
 */
export async function uploadFile<T = any>(path: string, file: File, options: AxiosRequestConfig = {}): Promise<T> {
  const formData = new FormData();
  formData.append('file', file);
  
  // Add any query params from options to formData as well
  if (options.params) {
    for (const key in options.params) {
      formData.append(key, options.params[key]);
    }
  }

  // Build URL with query params if provided
  let url = path;
  if (options.params) {
    const queryParams = new URLSearchParams();
    for (const key in options.params) {
      queryParams.append(key, options.params[key]);
    }
    url = `${path}?${queryParams.toString()}`;
  }

  const config: AxiosRequestConfig = {
    ...options,
    url: url,
    method: 'POST',
    headers: {
      'Content-Type': 'multipart/form-data',
      ...options.headers,
    },
    data: formData,
    // Remove params as they've already been added to the URL
    params: undefined
  };

  const response = await axiosInstance.request(config);
  return response.data;
}
